import { ElementType, lazy, Suspense } from "react";
import Spinner from "../components/Spinner/Spinner";
import { pageList } from "./pageList";

const modules: Record<string, () => Promise<{ default: ElementType }>> = {
    //Auth

    Login: () => import("@pages/Auth/components/Login/Login"),
    Registration: () =>
        import("@pages/Auth/components/Registration/Registration"),
    ForgotPassword: () =>
        import("@pages/Auth/components/ForgotPassword/ForgotPassword"),
    ResetPassword: () =>
        import("@pages/Auth/components/ResetPassword/ResetPassword"),
    PageNotFound: () =>
        import("@pages/Auth/components/PageNotFound/PageNotFound"),
    CreateNewPassword: () =>
        import("@pages/Auth/components/CreateNewPassword/CreateNewPassword"),
    TwoFactorAuthentication: () =>
        import(
            "@pages/Auth/components/TwoFactorAuthentication/TwoFactorAuthentication"
        ),

    //Dashboard

    Dashboard: () => import("@pages/Dashboard/container"),

    //// Administration

    EducationalInstitution: () =>
        import("@pages/Administration/EducationalInstitution/container"),
    InstituteSettings: () =>
        import(
            "@pages/Administration/EducationalInstitution/components/InstituteSettings/App/InstituteSettingsPages"
        ),
    UserAccessControl: () =>
        import("@pages/Administration/UserAccessControl/container"),
    Role: () => import("@pages/Administration/Role/container"),
    Permissions: () => import("@pages/Administration/Permissions/container"),
    ExpireAllPasswords: () =>
        import("@pages/Administration/ExpireAllPasswords/container"),
    ActionHistory: () =>
        import("@pages/Administration/ActionHistory/container"),

    //HR

    Employees: () => import("@pages/HR/Employees/container"),
    RolesAssociations: () => import("@pages/HR/RolesAssociations/container"),
    EmployeesOverview: () =>
        import("@pages/HR/Employees/components/EmployeesOverview/container"),
    EmployeesSettings: () =>
        import("@pages/HR/Employees/components/EmployeesSettings/container"),
    AcademicPositions: () => import("@pages/HR/AcademicPositions/container"),
    PositionsWorkingHours: () =>
        import("@pages/HR/PositionsWorkingHours/container"),

    //Students Management

    Students: () => import("@pages/StudentsManagement/Students/container"),
    StudentsOverview: () =>
        import(
            "@pages/StudentsManagement/Students/components/StudentsOverview/StudentsOverview"
        ),
    StudentsSettings: () =>
        import(
            "@pages/StudentsManagement/Students/components/StudentsSettings/StudentsSettings"
        ),
    StudyPaymentOptions: () =>
        import("@pages/StudentsManagement/StudyPaymentOptions/container"),

    // Educational Structure
    EducationalDegree: () =>
        import("@pages/EducationalStructure/EducationalDegree/container"),
    LearningFormat: () =>
        import("@pages/EducationalStructure/LearningFormat/container"),
    Faculty: () => import("@pages/EducationalStructure/Faculty/container"),
    Profession: () =>
        import("@pages/EducationalStructure/Profession/container"),
    AcademicDepartment: () =>
        import("@pages/EducationalStructure/AcademicDepartment/container"),
    Subject: () => import("../pages/EducationalStructure/Subject/container"),

    // Educational Settings
    AcademicYear: () =>
        import("@pages/EducationalSettings/AcademicYear/container"),
    AcademicTerm: () =>
        import("@pages/EducationalSettings/AcademicTerm/container"),
    StudyYears: () => import("@pages/EducationalSettings/StudyYears/container"),
    TeachingType: () =>
        import("@pages/EducationalSettings/TeachingType/container"),
    SubjectCheckType: () =>
        import("@pages/EducationalSettings/SubjectCheckType/container"),
    BaselineData: () =>
        import("@pages/EducationalSettings/BaselineData/container"),
    BaselineDataSetting: () =>
        import("@pages/EducationalSettings/BaselineData/container"),
    Qualification: () =>
        import("@pages/EducationalSettings/Qualification/container"),
    ModularSubject: () =>
        import("@pages/EducationalSettings/ModularSubject/container"),
    EducationalSectors: () =>
        import("@pages/EducationalSettings/EducationalSectors/container"),
    EducationalDuration: () =>
        import(
            "@pages/EducationalSettings/EducationalDegreeDuration/container"
        ),

    // Settings
    PasswordPolicies: () =>
        import("@pages/Settings/PasswordPolicies/container"),
    ExpireAllPassword: () =>
        import("@pages/Settings/ExpireAllPassword/container"),
    InstitutionalPreferences: () =>
        import("@pages/Settings/InstitutionalPreferences/container"),

    // Learning Management
    LearningPlan: () =>
        import("@pages/LearningManagement/LearningPlan/container"),
    LearningPlanRestore: () =>
        import(
            "@pages/LearningManagement/LearningPlan/components/LearningPlanRestore/App/LearningPlanRestore"
        ),
    LearningPlanOpen: () =>
        import(
            "@pages/LearningManagement/LearningPlan/components/LearningPlanOpen/App/LearningPlanOpen"
        ),
    LearningPlanSettings: () =>
        import(
            "@pages/LearningManagement/LearningPlan/components/LearningPlanSettings/LearningPlanSettings"
        ),
    Baseline: () => import("@pages/LearningManagement/Baseline/container"),
    BaselineItems: () =>
        import(
            "@pages/LearningManagement/Baseline/components/BaselineSettings/App/BaselineSettings"
        ),
    AcademicStructure: () =>
        import("@pages/LearningManagement/AcademicStructure/container"),
    AcademicStructureSettings: () =>
        import(
            "@pages/LearningManagement/AcademicStructure/components/AcademicStructureSettings/App/AcademicStructureSettings"
        ),
    Workload: () => import("@pages/LearningManagement/Workload/container"),
    WorkLoadSettings: () =>
        import(
            "@pages/LearningManagement/Workload/components/WorkloadSettings/App/WorkloadSettings"
        ),
    WorkloadItem: () =>
        import(
            "@pages/LearningManagement/Workload/components/WorkloadItem/App/WorkloadItem"
        ),
    Competences: () =>
        import("@pages/LearningManagement/Competences/container"),
    CompetencesSettings: () =>
        import(
            "@pages/LearningManagement/Competences/components/CompetencesSettings/App/CompetencesSettings"
        ),
    EducationalBlocks: () =>
        import("@pages/LearningManagement/EducationalBlocks/container"),
    EducationalSettings: () =>
        import(
            "@pages/LearningManagement/EducationalBlocks/EducationalBlocksPages/EducationalSettings/App/EducationalSettings"
        ),
    AcademicScheduling: () =>
        import("@pages/LearningManagement/AcademicScheduling/container"),
    SubjectGroups: () =>
        import("@pages/LearningManagement/SubjectGroups/container"),
    UnitedLessons: () =>
        import("@pages/LearningManagement/UnitedLessons/container"),
    UnitedItem: () =>
        import(
            "@pages/LearningManagement/UnitedLessons/components/UnitedItem/App/UnitedItem"
        ),
    ElectiveSubjects: () =>
        import("@pages/LearningManagement/ElectiveSubjects/container"),
    ElectiveItems: () =>
        import(
            "@pages/LearningManagement/ElectiveSubjects/components/ElectiveItems/App/ElectiveItems"
        ),
    NonWorkingDays: () =>
        import("@pages/LearningManagement/NonWorkingDays/container"),
    AcademicWeekSetup: () =>
        import(
            "@pages/LearningManagement/AcademicWeekSetup/container"
        ),

    // Languages Translations
    Languages: () => import("@pages/LanguageTranslations/Languages/container"),
    Translations: () =>
        import("@pages/LanguageTranslations/Translations/container"),
};

type TProps = {
    loading?: ElementType;
    error?: ElementType;
    factory: () => Promise<{ default: () => JSX.Element }>;
};
type TPages = Record<string, JSX.Element>;

export function lazyRoute({ factory }: TProps) {
    const Elem = lazy(factory);
    return (
        <Suspense fallback={<Spinner />}>
            <Elem />
        </Suspense>
    );
}

function mapPagesToModules(
    pages: string[],
    moduleList: Record<string, () => Promise<{ default: ElementType }>>
) {
    return pages.reduce((acc, page) => {
        const factory = moduleList[page];
        if (factory) {
            acc[page] = lazyRoute({
                factory: factory as () => JSX.Element["type"],
            });
        }
        return acc;
    }, {} as TPages);
}

export const importPages = mapPagesToModules(pageList, modules);
